import { PermissionCheck } from "../report-system/core/services/permission.service";

export interface NavbarMenuItem {
  id?: string;
  label: string;
  route?: any[] | string;
  queryParams?: Record<string, any>;
  actionId?: string;
  children?: NavbarMenuItem[];
  visible?: boolean;
  dividerAfter?: boolean;
  disabled?: boolean;
  placement?: string;
  linkClass?: string;
  menuClass?: string;
  permission?: PermissionCheck | PermissionCheck[];
  permissionMode?: "any" | "all";
  /**
   * Phone bottom bar only: render this branch's children as drill-in rows
   * rather than flattening them into inline groups. Used where the children
   * are themselves branches, so flattening would show empty group headings.
   */
  drillOnly?: boolean;
  /**
   * Desktop-only feature. The phone bottom sheet renders this row with a
   * "Web only" flag and does not act on a tap — the target screen has no
   * usable phone layout, so opening it would strand the user.
   */
  webOnly?: boolean;
}

export interface NavbarNotificationConfig {
  show: boolean;
  permission?: PermissionCheck | null;
}

export type NavbarVariant = "site-admin" | "site-user" | "multisite-user";

export interface NavbarConfigContext {
  userRole?: string | number | null;
  allocatedSite?: any;
  hasPfd?: boolean;
  showPrimaryMenus?: boolean;
  visitedUserManagementSites?: boolean;
}

export const NAVBAR_NOTIFICATION_PERMISSION: PermissionCheck =
  "notification-bell";

const topMenuClass = "dropdown-menu top-52px shadow user-menus border-none";
const topStaticMenuClass =
  "dropdown-menu bg-light top-52px shadow border-none user-menus static";
const reportsMenuClass =
  "dropdown-menu bg-light top-52px shadow user_role border-none user-menus static";
const submenuClass =
  "dropdown-menu bg-light user-menus sub-dropdown-menu user_role";
const opexSubmenuClass =
  "dropdown-menu bg-light user-menus sub-dropdown-menu user_role sub_opex_drop";

export function hasAnyRole(
  userRole: string | number | null | undefined,
  roles: string[],
): boolean {
  return roles.includes(String(userRole ?? ""));
}

export function buildNavbarMenuItems(
  variant: NavbarVariant,
  context: NavbarConfigContext,
): NavbarMenuItem[] {
  switch (variant) {
    case "site-admin":
      return buildSiteAdminMenuItems(context);
    case "multisite-user":
      return buildMultiSiteUserMenuItems(context);
    case "site-user":
    default:
      return buildSiteUserMenuItems(context);
  }
}

export function buildProfileMenuItems(
  variant: NavbarVariant,
  context: NavbarConfigContext,
): NavbarMenuItem[] {
  if (variant === "site-admin" && context.visitedUserManagementSites) {
    return [{ label: "You can't logout from here", disabled: true }];
  }

  return [
    {
      label: "My Data & Privacy",
      actionId: "myDataPrivacy",
      dividerAfter: true,
    },
    {
      label: "Audit Logs",
      actionId: "auditLogs",
      permission: "settings-audit-logs",
      dividerAfter: true,
    },
    { label: "Logout", actionId: "logout" },
  ];
}

export function buildNavbarNotificationConfig(
  variant: NavbarVariant,
  context: NavbarConfigContext,
): NavbarNotificationConfig {
  switch (variant) {
    case "site-admin":
    case "site-user":
    case "multisite-user":
      return {
        show: true,
        permission: NAVBAR_NOTIFICATION_PERMISSION,
      };
    default:
      return {
        show: false,
        permission: null,
      };
  }
}

function buildSiteAdminMenuItems(
  context: NavbarConfigContext,
): NavbarMenuItem[] {
  return [
    {
      label: "PLANT SETTINGS",
      id: "plantSettings",
      menuClass: topMenuClass,
      children: [
        {
          label: "USER",
          route: "/site/allsiteuser",
          dividerAfter: true,
          permission: "users-list",
        },
        {
          label: "PLANT CUSTOMIZATION",
          webOnly: true,
          actionId: "plantCustomization",
          permission: "site-settings",
          dividerAfter: true,
        },
        {
          label: "ADVANCED ALERTS",
          route: "/newalerts/alertlist",
          queryParams: { showTableOnly: false },
          permission: "alerts-list",
          dividerAfter: true,
        },
        {
          label: "SINGLE REPORT",
          permission: "reports-list",
          menuClass: submenuClass,
          children: [
            {
              label: "PROCESS",
              route: "/report-system/report-list/process",
              dividerAfter: true,
              permission: "reports-list",
            },
            {
              label: "OPEX",
              route: "/report-system/report-list/opex",
              permission: "reports-list",
            },
          ],
          dividerAfter: true,
        },
        {
          label: "EQUIPMENT",
          route: "/equipment/equipmentsetting",
          webOnly: true,
          dividerAfter: true,
          permission: "maint-equipment",
        },
        {
          label: "MANUAL DATA UPLOAD",
          permission: ["data-upload"],
          permissionMode: "any",
          menuClass: submenuClass,
          children: [
            {
              label: "MANAGE MANUAL DATA UPLOAD",
              route: "/uploadagent/managelab",
              dividerAfter: true,
              permission: "data-upload",
            },
            {
              label: "MANAGE USERS",
              route: "/uploadagent/managelabusers",
              permission: "data-upload",
            },
          ],
          dividerAfter: true,
        },
        {
          label: "DMS USER SETTING",
          actionId: "dmsUserSetting",
          dividerAfter: true,
          permission: "dms",
        },
        {
          label: "PURIFAI USER SETTING",
          actionId: "purifaiUserSetting",
          permission: "purifai",
        },
      ],
    },
    {
      label: "REPORTS",
      id: "reportsMenu",
      permission: ["dashboards-list", "reports-list"],
      permissionMode: "any",
      menuClass: reportsMenuClass,
      children: [
        {
          label: "PROCESS",
          permission: ["dashboards-list", "reports-list"],
          permissionMode: "any",
          menuClass: opexSubmenuClass,
          children: [
            {
              label: "MODULE",
              route: "/dashboard/processdashboard",
              dividerAfter: true,
              permission: "dashboards-list",
            },
            {
              label: "LIST",
              route: "/report-system/report-list/process",
              permission: "reports-list",
            },
          ],
          dividerAfter: true,
        },
        {
          label: "OPEX",
          permission: ["dashboards-list", "reports-list"],
          permissionMode: "any",
          menuClass: opexSubmenuClass,
          children: [
            {
              label: "MODULE",
              route: "/report-system/module-reports/opex",
              dividerAfter: true,
              permission: "dashboards-list",
            },
            {
              label: "LIST",
              route: "/report-system/report-list/opex",
              permission: "reports-list",
            },
          ],
        },
      ],
    },
    {
      label: "SPECIAL REPORTS",
      route: "/report-system/dashboard-group-list",
      permission: "dashboards-list",
    },
    {
      label: "ALERTS",
      route: "/newalerts/alertlist",
      queryParams: { showTableOnly: true },
      permission: "alerts-list",
    },
    {
      label: "DATA UPLOAD",
      id: "uploadMenu",
      permission: [
        "data-upload-history",
        "data-upload-csv",
        "data-upload-json",
        "data-upload",
      ],
      permissionMode: "any",
      menuClass: topStaticMenuClass,
      children: [
        {
          label: "PROCESS",
          route: "/site/uploads/process",
          dividerAfter: true,
          permission: "data-upload-csv",
        },
        {
          label: "OPEX",
          route: "/site/uploads/opex",
          dividerAfter: true,
          permission: "data-upload-csv",
        },
        {
          label: "SPECIAL LUT",
          route: "/site/dynamic-opex-pricing-list",
          dividerAfter: true,
          permission: "data-upload-csv",
        },
        {
          label: "MANUAL UPLOAD APP",
          route: "/uploadagent/managelabupload",
          dividerAfter: true,
          permission: "data-upload",
        },
        { label: "DMS", actionId: "dms", permission: "dms" },
      ],
    },
    {
      label: "PFD",
      route: "/site/pdfviewer",
      visible: !!context.hasPfd,
      permission: "data-upload",
    },
  ];
}

function buildSiteUserMenuItems(
  context: NavbarConfigContext,
): NavbarMenuItem[] {
  return [
    {
      label: "PLANT SETTINGS",
      id: "plantSettings",
      menuClass: topMenuClass,
      children: [
        {
          label: "USER",
          route: "/site/allsiteuser",
          dividerAfter: true,
          permission: "users-list",
        },
        {
          label: "PLANT CUSTOMIZATION",
          webOnly: true,
          route: ["/site/editsite", context.allocatedSite],
          permission: "sites-edit",
          dividerAfter: true,
        },
        {
          label: "ALERTS",
          permission: "alerts-list",
          menuClass: submenuClass,
          children: [
            {
              label: "PM ALERTS",
              route: "/alerts/pmalerts",
              dividerAfter: true,
              permission: "alerts-list",
            },
            {
              label: "MODULE ALERTS",
              route: "/alerts/modulealerts",
              dividerAfter: true,
              permission: "alerts-list",
            },
            {
              label: "PARAMETERS ALERTS",
              route: "/alerts/parameteralerts",
              permission: "alerts-list",
            },
          ],
          dividerAfter: true,
        },
        {
          label: "SINGLE REPORT",
          permission: "reports-list",
          menuClass: submenuClass,
          children: [
            {
              label: "PROCESS",
              route: "/reportlibrary/editreport/process",
              dividerAfter: true,
              permission: "reports-list",
            },
            {
              label: "OPEX",
              route: "/reportlibrary/editreport/opex",
              permission: "reports-list",
            },
          ],
          dividerAfter: true,
        },
        {
          label: "EQUIPMENT",
          route: "/equipment/equipmentsetting",
          webOnly: true,
          dividerAfter: true,
          permission: "maint-equipment",
        },
        {
          label: "MANUAL DATA UPLOAD",
          permission: [
            "data-upload-history",
            "data-upload-csv",
            "data-upload-json",
          ],
          permissionMode: "any",
          menuClass: submenuClass,
          children: [
            {
              label: "MANAGE MANUAL DATA UPLOAD",
              route: "/uploadagent/managelab",
              dividerAfter: true,
              permission: "data-upload-history",
            },
            {
              label: "MANAGE USERS",
              route: "/uploadagent/managelabusers",
              permission: "users-list",
            },
          ],
        },
      ],
    },
    {
      label: "REPORTS",
      id: "reportsMenu",
      permission: ["dashboards-list", "reports-list"],
      permissionMode: "any",
      menuClass: reportsMenuClass,
      children: [
        {
          label: "PROCESS",
          permission: ["dashboards-list", "reports-list"],
          permissionMode: "any",
          menuClass: opexSubmenuClass,
          children: [
            {
              label: "MODULE",
              route: "/dashboard/processdashboard",
              dividerAfter: true,
              permission: "dashboards-list",
            },
            {
              label: "LIST",
              route: "/reportlibrary/editreport/process",
              permission: "reports-list",
            },
          ],
          dividerAfter: true,
        },
        {
          label: "OPEX",
          permission: ["dashboards-list", "reports-list"],
          permissionMode: "any",
          menuClass: opexSubmenuClass,
          children: [
            {
              label: "MODULE",
              route: "/dashboard/opexdashboard",
              dividerAfter: true,
              permission: "dashboards-list",
            },
            {
              label: "LIST",
              route: "/dashboard/customeOpexDashboard",
              permission: "reports-list",
            },
          ],
        },
      ],
    },
    {
      label: "SPECIAL REPORTS",
      route: "/mixdashboard/mix-report-list",
      permission: "dashboards-list",
    },
    {
      label: "ALERTS",
      id: "alertsMenu",
      permission: ["alerts-list", "alerts-history"],
      permissionMode: "any",
      menuClass: topStaticMenuClass,
      children: [
        {
          label: "OPEN",
          route: "/alerts/openalert",
          dividerAfter: true,
          permission: "alerts-list",
        },
        {
          label: "HISTORY",
          route: "/alerts/historyalert",
          permission: "alerts-history",
        },
      ],
    },
    {
      label: "DATA UPLOAD",
      id: "uploadMenu",
      permission: [
        "data-upload-history",
        "data-upload-csv",
        "data-upload-json",
      ],
      permissionMode: "any",
      menuClass: topStaticMenuClass,
      children: [
        {
          label: "PROCESS",
          route: "/site/uploads/process",
          dividerAfter: true,
          permission: "data-upload-csv",
        },
        {
          label: "OPEX",
          route: "/site/uploads/opex",
          dividerAfter: true,
          permission: "data-upload-json",
        },
        {
          label: "SPECIAL LUT",
          route: "/site/dynamic-opex-pricing-list",
          permission: "data-upload-history",
        },
      ],
    },
    {
      label: "PFD",
      route: "/site/pdfviewer",
      visible: !!context.hasPfd,
    },
  ];
}

function buildMultiSiteUserMenuItems(
  context: NavbarConfigContext,
): NavbarMenuItem[] {
  return buildSiteUserMenuItems(context).map((item) => ({
    ...item,
    visible: context.showPrimaryMenus !== false && item.visible !== false,
  }));
}
